import { Button, Card, Form, Result, Row, Col, Spin } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Typography } from "antd";
import { CheckCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { EditFormContext } from "./PublicEmployeeEditForm";
import AppContext from "../../AppContext";
import axios from "axios";
import { BASE_URL } from "../../Constants";

const { Title } = Typography;

const PaymentSuccess = () => {
  const { t } = useTranslation();
  // const { formData: employeeData } = useContext(EditFormContext);
  const editFormContext = useContext(EditFormContext);
  const employeeData = editFormContext?.formData;
  const { isLoggedIn } = useContext(AppContext);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planOptions, setPlanOptions] = useState([]);

  const loadPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/Lookup/plans`);
      if (res.status === 200) {
        setPlanOptions(res.data);
        return res.data;
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      return [];
      //   message.error("Failed to load subscription plans. Please try again.");
    } finally {
      setPlansLoading(false);
    }
  };

  const getPaymentDetailsByRef = async (cartId, plans) => {
    try {
      var res = await axios.get(`${BASE_URL}/Payment/transaction/cart/${cartId}`);
      if (res.status === 200 && res.data) {
        const selectedPlan = plans.find((p) => p.id === res.data.planId);
        const paymentStatus = res.data.status?.toLowerCase();
        const isSuccess = paymentStatus === 'success' || paymentStatus === 'completed';
        
        const planAmount = parseFloat(res.data.planAmount) || 0;
        const finalAmount = parseFloat(res.data.finalAmount) || planAmount;
        const discountAmount = planAmount - finalAmount;
        const hasCoupon = res.data.couponId && discountAmount > 0;
        
        setPaymentDetails({
          type: "payment",
          planName: selectedPlan?.name || t("paymentSuccess.premiumPlanFallback"),
          planAmount: planAmount,
          finalAmount: finalAmount,
          discountAmount: discountAmount,
          amount: finalAmount, // Keep for backward compatibility
          transactionId: res.data.transactionReference || res.data.id || null,
          couponId: res.data.couponId || null,
          hasCoupon: hasCoupon,
          status: paymentStatus,
          isSuccess: isSuccess,
          failureReason: res.data.failureReason || res.data.responseMessage || null,
          responseCode: res.data.responseCode || null,
          // New signup detection — already present on every transaction record
          productType: res.data.productType || null,
          customerEmail: res.data.customerDetails?.email || null,
          // Server-side free completion (valid trial coupon, or a coupon covering the full price)
          paymentWaived: res.data.paymentWaived || false,
          waiveReason: res.data.waiveReason || null,
          trialDays: res.data.trialDays || null,
        });
      }
    } catch (error) {
      console.error("Error fetching payment details:", error);
    }
  };

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const ref = searchParams.get("transactionRef");
      const cartId = searchParams.get("cartId");
      const plans = await loadPlans();
      if (cartId) {
        await getPaymentDetailsByRef(cartId, plans);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setIsLoading(false);
    }
  };
  // Handle URL parameters for payment success and reference number
  useEffect(() => {
    loadInitialData();
  }, [searchParams]);

  // Function to clear payment state and URL parameters
  const clearPaymentState = () => {
    setPaymentDetails(null);
    setReferenceNumber(null);
    setSearchParams({});
  };

  const handleBackToCard = () => {
    setSelectedPlan(null);
    setAppliedCoupon(null);
    form.resetFields();

    // Clear payment state and URL parameters
    clearPaymentState();

    // Navigate back to subscription tab without parameters
    navigate("/subscription", { replace: true });
  };

  const handleViewCard = () => {
    // Individual Digital Card users are PublicEmployee records with no corporate Employee,
    // so the corporate /employee-id/{id} route (GET /api/Employee/digital-card/{id}) 500s
    // for them. "/" already renders this user's own HomeCard / PublicEmployeeEditForm.
    navigate("/");
  };

  // Custom loading component
  const LoadingComponent = () => (
    <div className="bg-white rounded-lg shadow md:p-6 p-3">
      <div className="flex flex-col items-center justify-center py-20">
        <Spin 
          size="large" 
          indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />}
        />
        <div className="mt-6 text-center">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            {t("paymentSuccess.loading.title")}
          </h3>
          <p className="text-gray-500">
            {t("paymentSuccess.loading.description")}
          </p>
        </div>
        <div className="mt-4 flex space-x-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      </div>
    </div>
  );

  // Show loading state while fetching data
  if (isLoading) {
    return <LoadingComponent />;
  }

  // Result copy. Four success shapes (paid digital-card, trial coupon, full-discount coupon,
  // legacy non-digital-card) plus two failure shapes (failed first signup while not logged in
  // -> restart signup, not /subscription; logged-in renewal failure -> Try Again).
  const isDigitalCard = paymentDetails?.productType === "digital-card";
  const isWedding = paymentDetails?.productType === "wedding";
  const isEvent = paymentDetails?.productType === "event";
  const isTrial = paymentDetails?.waiveReason === "trial";
  const isFullDiscount = paymentDetails?.waiveReason === "full-discount";
  const emailForCopy = paymentDetails?.customerEmail || "your email address";

  let resultTitle = "";
  let resultSubTitle = "";
  let resultExtra = [];
  // Only overridden for the Wedding/Event "status could not be confirmed" case below —
  // everywhere else keeps the existing isSuccess-based success/error framing.
  let resultStatus = null;

  if (paymentDetails) {
    if (paymentDetails.isSuccess && isDigitalCard && isTrial) {
      resultTitle = t("paymentSuccess.trial.title");
      resultSubTitle = t("paymentSuccess.trial.subtitle", { days: paymentDetails.trialDays || "", email: emailForCopy });
      resultExtra = [
        <Button key="login" type="primary" onClick={() => navigate("/")}>
          {t("paymentSuccess.buttons.continueToLogin")}
        </Button>,
      ];
    } else if (paymentDetails.isSuccess && isDigitalCard && isFullDiscount) {
      resultTitle = t("paymentSuccess.fullDiscount.title");
      resultSubTitle = t("paymentSuccess.fullDiscount.subtitle", { email: emailForCopy });
      resultExtra = [
        <Button key="login" type="primary" onClick={() => navigate("/")}>
          {t("paymentSuccess.buttons.continueToLogin")}
        </Button>,
      ];
    } else if (paymentDetails.isSuccess && isDigitalCard) {
      resultTitle = t("paymentSuccess.digitalCard.title");
      resultSubTitle = t("paymentSuccess.digitalCard.subtitle", { email: emailForCopy });
      resultExtra = [
        <Button key="login" type="primary" onClick={() => navigate("/")}>
          {t("paymentSuccess.buttons.continueToLogin")}
        </Button>,
      ];
    } else if (paymentDetails.isSuccess && isWedding) {
      resultTitle = t("paymentSuccess.wedding.title");
      resultSubTitle = t("paymentSuccess.wedding.subtitle", { plan: paymentDetails.planName, email: emailForCopy });
      resultExtra = [
        <Button key="login" type="primary" onClick={() => navigate("/")}>
          {t("paymentSuccess.buttons.goToWeddingEvents")}
        </Button>,
      ];
    } else if (paymentDetails.isSuccess && isEvent) {
      resultTitle = t("paymentSuccess.event.title");
      resultSubTitle = t("paymentSuccess.event.subtitle", { plan: paymentDetails.planName, email: emailForCopy });
      resultExtra = [
        <Button key="login" type="primary" onClick={() => navigate("/")}>
          {t("paymentSuccess.buttons.goToEventManagement")}
        </Button>,
      ];
    } else if (paymentDetails.isSuccess) {
      // Legacy / missing productType (e.g. corporate subscription renewal) — unchanged.
      resultTitle = t("paymentSuccess.generic.title");
      resultSubTitle = t("paymentSuccess.generic.subtitle", { amount: paymentDetails?.finalAmount?.toFixed(2) }) +
        (paymentDetails.hasCoupon ? t("paymentSuccess.generic.savedWithCoupon", { amount: paymentDetails.discountAmount?.toFixed(2) }) : "");
      resultExtra = [
        <Button key="back" onClick={handleBackToCard}>
          {t("paymentSuccess.buttons.backToSubscription")}
        </Button>,
        <Button key="view" type="primary" onClick={handleViewCard}>
          {t("paymentSuccess.buttons.viewDigitalCard")}
        </Button>,
      ];
    } else if (isDigitalCard && !isLoggedIn) {
      // Failed first commercial signup: no account exists, so /subscription is a dead end.
      resultTitle = t("paymentSuccess.notCompletedNoAccount.title");
      resultSubTitle = t("paymentSuccess.notCompletedNoAccount.subtitle", {
        email: emailForCopy,
        reasonSuffix: paymentDetails.failureReason ? t("paymentSuccess.notCompletedNoAccount.reasonSuffix", { reason: paymentDetails.failureReason }) : "",
      });
      resultExtra = [];
    } else if (isWedding || isEvent) {
      // Unlike Digital Card, the Wedding/Event account, virtual company and pending_payment
      // entitlement already exist before payment (see SignUpService.ProvisionEventAccountAsync),
      // so a failed/incomplete payment here never needs a "restart signup" message — the
      // existing account and package are simply still waiting to be paid for, and the same
      // /Payment/initiate flow already used by My Packages' "Pay & Activate Now" button can be
      // used to retry. /my-packages resolves correctly whether or not the user is currently
      // logged in: if not, App.js's catch-all route sends them through the existing
      // ProtectedRoute → login → redirect-back flow first.
      const packageLabel = isWedding ? t("paymentSuccess.packageLabel.wedding") : t("paymentSuccess.packageLabel.event");
      const goToMyPackagesButton = (
        <Button key="mypackages" type="primary" onClick={() => navigate("/my-packages")}>
          {t("paymentSuccess.buttons.goToMyPackages")}
        </Button>
      );

      if (paymentDetails.status === "failed") {
        resultTitle = t("paymentSuccess.unsuccessful.title");
        resultSubTitle = t("paymentSuccess.unsuccessful.subtitle", {
          packageLabel,
          reasonSuffix: paymentDetails.failureReason ? t("paymentSuccess.unsuccessful.reasonSuffix", { reason: paymentDetails.failureReason }) : "",
        });
        resultExtra = [
          <Button key="retry" onClick={() => navigate("/my-packages")}>
            {t("paymentSuccess.buttons.tryAgain")}
          </Button>,
          goToMyPackagesButton,
        ];
      } else if (paymentDetails.status === "cancelled") {
        resultTitle = t("paymentSuccess.notCompletedSaved.title");
        resultSubTitle = t("paymentSuccess.notCompletedSaved.subtitle", { packageLabel });
        resultExtra = [
          <Button key="retry" onClick={() => navigate("/my-packages")}>
            {t("paymentSuccess.buttons.tryAgain")}
          </Button>,
          goToMyPackagesButton,
        ];
      } else {
        // Status is Pending/Initiated (or anything else not yet final) — the callback didn't
        // establish a definite outcome. Never claim a failure the backend hasn't confirmed.
        resultStatus = "warning";
        resultTitle = t("paymentSuccess.statusUnknown.title");
        resultSubTitle = t("paymentSuccess.statusUnknown.subtitle", { packageLabel });
        resultExtra = [goToMyPackagesButton];
      }
    } else {
      resultTitle = t("paymentSuccess.failed.title");
      resultSubTitle = t("paymentSuccess.failed.subtitle", {
        amount: paymentDetails?.finalAmount?.toFixed(2),
        reasonSuffix: paymentDetails.failureReason ? t("paymentSuccess.failed.reasonSuffix", { reason: paymentDetails.failureReason }) : "",
      });
      resultExtra = [
        <Button key="back" onClick={handleBackToCard}>
          {t("paymentSuccess.buttons.backToSubscription")}
        </Button>,
        <Button
          key="retry"
          type="primary"
          onClick={() => navigate("/subscription")}
        >
          {t("paymentSuccess.buttons.tryAgain")}
        </Button>,
      ];
    }
  }

  return (
    <div className="bg-white rounded-lg shadow md:p-6 p-3">
      {/* Show error if no payment details */}
      {!paymentDetails && (
        <Result
          status="warning"
          title={t("paymentSuccess.noPaymentInfo.title")}
          subTitle={t("paymentSuccess.noPaymentInfo.subtitle")}
          extra={[
            <Button
              key="back"
              type="primary"
              onClick={() => navigate("/subscription")}
            >
              {t("paymentSuccess.noPaymentInfo.button")}
            </Button>,
          ]}
        />
      )}

      {/* Show success or failure page based on payment status */}
      {paymentDetails && (
        <>
          <Result
            status={resultStatus || (paymentDetails.isSuccess ? "success" : "error")}
            title={resultTitle}
            subTitle={resultSubTitle}
            extra={resultExtra}
          />

          {/* Payment Details Card */}
          <Card className="mt-6 bg-gray-50">
            <Title level={5} className="mb-4">
              {paymentDetails.isSuccess ? (
                <CheckCircleOutlined className="text-green-500 mr-2" />
              ) : (
                <span className="text-red-500 mr-2">✕</span>
              )}
              {paymentDetails.isSuccess ? t("paymentSuccess.details.subscriptionDetails") : t("paymentSuccess.details.transactionDetails")}
            </Title>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-700">{t("paymentSuccess.details.plan")}</span>
                    <div className="text-lg font-semibold text-blue-600">
                      {paymentDetails?.planName}
                    </div>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">{t("paymentSuccess.details.customer")}</span>
                    <div className="text-gray-900">
                      {employeeData?.fullName ||
                        employeeData?.name ||
                        (employeeData?.firstName && employeeData?.lastName ? `${employeeData?.firstName} ${employeeData?.lastName}` : employeeData?.firstName || '') ||
                        t("paymentSuccess.details.customerFallback")}
                    </div>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">{t("paymentSuccess.details.email")}</span>
                    <div className="text-gray-900">{employeeData?.email || paymentDetails?.customerEmail || ""}</div>
                  </div>
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-700">
                      {t("paymentSuccess.details.paymentStatus")}
                    </span>
                    <div className={`text-lg font-semibold ${paymentDetails.isSuccess ? 'text-green-600' : 'text-red-600'}`}>
                      {paymentDetails.status?.toUpperCase()}
                    </div>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">
                      {t("paymentSuccess.details.transactionId")}
                    </span>
                    <div className="text-gray-900 font-mono text-sm">
                      {referenceNumber || paymentDetails?.transactionId}
                    </div>
                  </div>

                  {!paymentDetails.isSuccess && paymentDetails.responseCode && (
                    <div>
                      <span className="font-medium text-gray-700">
                        {t("paymentSuccess.details.responseCode")}
                      </span>
                      <div className="text-red-600 font-mono text-sm">
                        {paymentDetails.responseCode}
                      </div>
                    </div>
                  )}

                  {!paymentDetails.isSuccess && paymentDetails.failureReason && (
                    <div>
                      <span className="font-medium text-gray-700">
                        {t("paymentSuccess.details.failureReason")}
                      </span>
                      <div className="text-red-600">
                        {paymentDetails.failureReason}
                      </div>
                    </div>
                  )}

                  <div>
                    <span className="font-medium text-gray-700">
                      {t("paymentSuccess.details.planPrice")}
                    </span>
                    <div className={`text-lg font-semibold ${paymentDetails.hasCoupon ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                      {paymentDetails?.planAmount?.toFixed(2)} SAR
                    </div>
                  </div>

                  {paymentDetails.hasCoupon && (
                    <div>
                      <span className="font-medium text-gray-700">
                        {t("paymentSuccess.details.couponDiscount")}
                      </span>
                      <div className="text-green-600 font-semibold">
                        - {paymentDetails.discountAmount?.toFixed(2)} SAR
                      </div>
                    </div>
                  )}

                  <div className={paymentDetails.hasCoupon ? 'pt-2 border-t border-gray-200' : ''}>
                    <span className="font-medium text-gray-700">
                      {paymentDetails.isSuccess ? t("paymentSuccess.details.amountPaid") : t("paymentSuccess.details.attemptedAmount")}
                    </span>
                    <div className={`text-2xl font-bold ${paymentDetails.isSuccess ? 'text-green-600' : 'text-gray-600'}`}>
                      {paymentDetails?.finalAmount?.toFixed(2)} SAR
                    </div>
                    {paymentDetails.hasCoupon && paymentDetails.isSuccess && (
                      <div className="text-xs text-green-600 mt-1">
                        {t("paymentSuccess.details.savedAmount", { amount: paymentDetails.discountAmount?.toFixed(2) })}
                      </div>
                    )}
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">{t("paymentSuccess.details.date")}</span>
                    <div className="text-gray-900">
                      {new Date().toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Next Steps - Only show for successful payments */}
          {paymentDetails.isSuccess && paymentDetails.productType === 'digital-card' && (
            <Card className="mt-4">
              <Title level={5} className="mb-3">
                {t("paymentSuccess.nextStepsTitle")}
              </Title>
              <div className="space-y-2 text-gray-700">
                <p>{t("paymentSuccess.digitalCard.next1", { email: "" })} <strong>{paymentDetails.customerEmail}</strong></p>
                <p>{t("paymentSuccess.digitalCard.next2")}</p>
                <p>{t("paymentSuccess.digitalCard.next3")}</p>
              </div>
            </Card>
          )}
          {paymentDetails.isSuccess && isWedding && (
            <Card className="mt-4">
              <Title level={5} className="mb-3">
                {t("paymentSuccess.nextStepsTitle")}
              </Title>
              <div className="space-y-2 text-gray-700">
                <p>{t("paymentSuccess.wedding.next1", { email: "" })} <strong>{emailForCopy}</strong></p>
                <p>{t("paymentSuccess.wedding.next2")}</p>
                <p>{t("paymentSuccess.wedding.next3")}</p>
              </div>
            </Card>
          )}
          {paymentDetails.isSuccess && isEvent && (
            <Card className="mt-4">
              <Title level={5} className="mb-3">
                {t("paymentSuccess.nextStepsTitle")}
              </Title>
              <div className="space-y-2 text-gray-700">
                <p>{t("paymentSuccess.event.next1", { email: "" })} <strong>{emailForCopy}</strong></p>
                <p>{t("paymentSuccess.event.next2")}</p>
                <p>{t("paymentSuccess.event.next3")}</p>
              </div>
            </Card>
          )}
          {paymentDetails.isSuccess && !isDigitalCard && !isWedding && !isEvent && (
            <Card className="mt-4">
              <Title level={5} className="mb-3">
                {t("paymentSuccess.nextStepsTitle")}
              </Title>
              <div className="space-y-2 text-gray-700">
                <p>{t("paymentSuccess.generic.next1")}</p>
                <p>{t("paymentSuccess.generic.next2")}</p>
                <p>{t("paymentSuccess.generic.next3")}</p>
              </div>
            </Card>
          )}

          {/* Troubleshooting - Only show for confirmed failed payments, never for a status
              that couldn't be confirmed (that's not necessarily a card/funds issue). */}
          {!paymentDetails.isSuccess && resultStatus !== "warning" && (
            <Card className="mt-4 border-red-200">
              <Title level={5} className="mb-3 text-red-600">
                {t("paymentSuccess.troubleshooting.title")}
              </Title>
              <div className="space-y-2 text-gray-700">
                <p>• {t("paymentSuccess.troubleshooting.line1")}</p>
                <p>• {t("paymentSuccess.troubleshooting.line2")}</p>
                <p>• {t("paymentSuccess.troubleshooting.line3")}</p>
                <p>• {t("paymentSuccess.troubleshooting.line4")}</p>
                <p>• {t("paymentSuccess.troubleshooting.line5")}</p>
              </div>
            </Card>
          )}

          {/* Support Section */}
          <div className="mt-6 text-center text-gray-500 text-sm">
            <p>
              {t("paymentSuccess.support.needHelp")}{" "}
              <a
                href="https://future-horses.com/contact/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
              >
                {t("paymentSuccess.support.contactSupport")}
              </a>
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
